/**
 * Missing-metadata reporter — v1.2.12 redesign.
 *
 * Up to v1.2.11 this module ran an on-device scan loop that hammered TMDB
 * for every title without metadata, caching results in localStorage. On
 * Smart TVs with a 50k-movie playlist that loop:
 *   • churned through 50,000 HTTP requests on every device,
 *   • bloated localStorage past its 5 MB quota,
 *   • and contributed to the "Data cannot be cloned, out of memory"
 *     IDBObjectStore crash by piling up cache writes.
 *
 * The new behaviour: the device just builds a deduped list of items
 * that don't have a provider-supplied tmdb_id and POSTs the list to
 * `/api/player/missing-metadata` (in 500-item chunks). The backend keeps
 * the consolidated backlog and the admin can manually backfill metadata
 * for the long tail from the Missing Metadata page.
 *
 * The public API surface (`startScan`, `cancelScan`, `subscribeToScan`,
 * `getScanState`) is unchanged so `PlayerSettings.jsx` keeps working.
 * The state-machine shape is also preserved (`done`, `total`, `filled`,
 * `current`) — `filled` now means "successfully reported" instead of
 * "TMDB metadata cached".
 */
import { reportMissingMetadata } from "./tmdbCache";

const INITIAL = {
  running: false,
  done: 0,
  total: 0,
  filled: 0, // # reported successfully (kept named for UI compat)
  current: "",
  result: null,
};

let _state = { ...INITIAL };
const _listeners = new Set();
let _cancelled = false;
let _running = false;

function emit() {
  const snap = { ..._state };
  for (const fn of _listeners) {
    try {
      fn(snap);
    } catch {
      /* listener crash isolation */
    }
  }
}

export function getScanState() {
  return { ..._state };
}

export function subscribeToScan(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/**
 * Push the list of items missing metadata to the admin backlog.
 * `channels` is the active playlist's channel list — same as the old
 * scanner accepted. Returns false if a report is already in flight.
 */
export function startScan(channels) {
  if (_running) return false;
  _cancelled = false;
  _running = true;
  _state = { ..._state, running: true, done: 0, total: 0, filled: 0, current: "", result: null };
  emit();

  reportMissingMetadata(channels, ({ done, total, reported }) => {
    if (_cancelled) return;
    _state = {
      ..._state,
      done,
      total,
      filled: reported,
      current: "",
    };
    emit();
  })
    .then(({ reported, total }) => {
      _state = {
        running: false,
        done: total,
        total,
        filled: reported,
        current: "",
        result: _cancelled ? null : { filled: reported, total, skipped: 0 },
      };
      emit();
    })
    .catch(() => {
      _state = { ..._state, running: false, result: { filled: 0, total: _state.total, skipped: 0 } };
      emit();
    })
    .finally(() => {
      _running = false;
    });

  return true;
}

export function cancelScan() {
  _cancelled = true;
  _running = false;
  _state = { ...INITIAL };
  emit();
}
